<template>
  <div class="rounded-lg border bg-card p-6">
    <div>
      <h3 class="text-base font-semibold text-foreground">{{ $ts('checkout.contactDetails.title') }}</h3>
      <p class="mt-0.5 text-sm text-muted-foreground">{{ $ts('checkout.contactDetails.subtitle') }}</p>
    </div>

    <div class="mt-5 space-y-4">
      <!-- Full name -->
      <div class="space-y-1.5">
        <Label for="fullName" class="text-sm font-medium">{{ $ts('checkout.contactDetails.fullName') }}</Label>
        <Input
          id="fullName"
          :model-value="modelValue.fullName"
          :placeholder="$ts('checkout.contactDetails.fullNamePlaceholder')"
          @update:model-value="update('fullName', $event)"
        />
      </div>

      <!-- Email -->
      <div class="space-y-1.5">
        <Label for="email" class="text-sm font-medium">{{ $ts('checkout.contactDetails.email') }}</Label>
        <Input
          id="email"
          type="email"
          :model-value="modelValue.email"
          :placeholder="$ts('checkout.contactDetails.emailPlaceholder')"
          @update:model-value="update('email', $event)"
        />
      </div>

      <!-- Phone -->
      <div class="space-y-1.5">
        <Label for="phone" class="text-sm font-medium">{{ $ts('checkout.contactDetails.phone') }}</Label>
        <Input
          id="phone"
          type="tel"
          :model-value="modelValue.phone"
          :placeholder="$ts('checkout.contactDetails.phonePlaceholder')"
          @update:model-value="update('phone', $event)"
        />
      </div>

      <!-- Special requests -->
      <div class="space-y-1.5">
        <Label for="specialRequests" class="text-sm font-medium">
          {{ $ts('checkout.contactDetails.specialRequests') }}
          <span class="font-normal text-muted-foreground">{{ $ts('checkout.contactDetails.specialRequestsOptional') }}</span>
        </Label>
        <Input
          id="specialRequests"
          :model-value="modelValue.specialRequests"
          :placeholder="$ts('checkout.contactDetails.specialRequestsPlaceholder')"
          @update:model-value="update('specialRequests', $event)"
        />
      </div>
    </div>

    <!-- Sign in link -->
    <p class="mt-4 text-sm text-muted-foreground">
      {{ $ts('checkout.contactDetails.alreadyHaveAccount') }}
      <NuxtLink :to="$localeRoute('/account/login')" class="font-medium text-primary hover:underline">
        {{ $ts('checkout.contactDetails.signIn') }}
      </NuxtLink>
    </p>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '#imports';
import Label from '#bubba/shared/components/ui/label/Label.vue';
import Input from '#bubba/shared/components/ui/input/Input.vue';

const { $ts, $localeRoute } = useI18n();

export interface ContactDetails {
  fullName: string;
  email: string;
  phone: string;
  specialRequests: string;
}

const props = defineProps<{
  modelValue: ContactDetails;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: ContactDetails];
}>();

function update(field: keyof ContactDetails, value: string | number) {
  emit('update:modelValue', { ...props.modelValue, [field]: String(value) });
}
</script>
